import React, {Fragment} from 'react';
import {Link, useLocation} from 'react-router-dom';

import {AlertInfo, CardList, SelectLocationContainer, StyledGrid} from 'presentation/components';
import {QueryParamsEnum, QueryParamsForTaggingEnum} from 'presentation/constants';
import {
	useAccountInformation,
	useMediaQueries,
	useQueryParams,
} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {
	CHANGE_PLAN_LOCATION_FLOW,
	FlowEnumWithoutProfile,
	FlowTypeEnum,
	formatText,
	getCityPath,
	getLocalityPath,
	getPageHeaderTitle,
} from 'presentation/utils';

import {Box, Typography} from '@mui/material';
import {AppointmentBookingEnum, FinishWithoutCompleteFlowEnum} from 'main/analytics/gaTagsEnum';
import {Loading} from 'presentation/components/Loading/Loading';
import {
	BookingEmptyState,
	BookingErrorState,
	BookingNotFoundDialog,
	BookingNotFoundState,
} from 'presentation/pages/private/booking/components';
import {BookingNotFoundVariantEnum} from 'presentation/pages/private/booking/components/BookingNotFoundDialog/types';
import {BookingNotFoundStateVariantEnum} from 'presentation/pages/private/booking/components/BookingNotFoundState/types';
import {FlowEnum, PATH_TITLE} from 'presentation/pages/private/booking/constants';

import {useGetHealthCardQuery} from 'presentation/pages/private/health-card/queries/useGetHealthCardQuery';
import {useGetStatesQuery} from '../../hooks/useGetStatesQuery';
import {RecentLocationCard} from '../RecentLocationCard';
import {UfSelectionProps} from './types';
import {useNotFound} from 'presentation/pages/private/hooks/useNotFound';
import {textHandle} from 'presentation/utils/textHandle';
import {getHealthStepPosition} from 'presentation/modules/booking/health/utils/getHealthStepPosition';
import {NotFoundDialog} from 'presentation/modules/guide/shared/ui/components/DistrictSelection/components/NotFoundDialog';
import {NotFoundVariantEnum} from 'presentation/modules/guide/shared/ui/components/DistrictSelection/components/NotFoundDialog/types';

const {
	state,
	emptySearchState,
	locality: {geolocNotFoundTitle, geolocNotFoundDescription},
} = translations['pt-br'].privateFlow.profile.locationSelection;
const {notFoundUf} = translations['pt-br'].privateFlow.requestAuthorizations.emptySearch;
const {booking} = translations['pt-br'].others.sidebar;
const {selectUf} = translations['pt-br'].commons;

export function UfList({flowType, handleBack, ufDataTagId}: UfSelectionProps) {
	const isOdonto = flowType === FlowEnum.ODONTO;
	const isAuthorizationFlow = flowType === FlowEnum.AUTHORIZATIONS;
	const isHealthFlow = flowType === FlowEnum.HEALTH;
	const isExamFlow = flowType === FlowEnum.EXAM;
	const isGuideFlow = flowType === FlowEnum.HEALTH_GUIDE || flowType === FlowEnum.ODONTO_GUIDE;
	const isChangePlanFlow = flowType === CHANGE_PLAN_LOCATION_FLOW;
	const isHealthOrExamFlow = isExamFlow || isHealthFlow;
	const isBookingFlow = isHealthOrExamFlow || isOdonto;
	const shouldShowLocation = isBookingFlow || isGuideFlow || isAuthorizationFlow || isChangePlanFlow;
	const flowTypeForBookingComponents: FlowEnumWithoutProfile = isChangePlanFlow
		? FlowEnum.HEALTH
		: flowType;
	const flowTypeForBookingDialog: FlowTypeEnum =
		isChangePlanFlow || isGuideFlow ? FlowEnum.HEALTH : flowType;
	const {pathname, state: useLocationState} = useLocation();
	const {handleCallChatRoom, handleGuideNav, notFoundModalIsOpen, setNotFoundModalIsOpen} =
		useNotFound();
	const {SEARCH_PARAMS} = useQueryParams();
	const {currentLocation, beneficiary} = useAccountInformation();
	const {isMobile} = useMediaQueries();
	const {ufCode, query} = SEARCH_PARAMS;

	const pageTitle = getPageHeaderTitle({internalPaths: PATH_TITLE, mainPath: booking});
	const bookingCityPath = getCityPath(flowType);
	const bookingLocalityPath = getLocalityPath(flowType);
	const {data: healthCard, isLoading: isHealthCardLoading} = useGetHealthCardQuery();
	const cdPlanoANSOdonto =
		healthCard?.registroPlanoAnsOdonto?.trim() || healthCard?.registroPlanoAns;
	const nuRegistroPlano = beneficiary?.nuRegistroPlano;

	const {
		data: ufList,
		isLoading: loading,
		isError: error,
		refetch: refresh,
	} = useGetStatesQuery({
		flowType,
		cdPlanoANSOdonto,
		nuRegistroPlano,
	});

	const getEmptyDescription = () => {
		if (isGuideFlow) {
			return emptySearchState.notFoundAllGuideFlow;
		}

		if (isAuthorizationFlow) {
			return notFoundUf;
		}
		if (isOdonto) {
			return emptySearchState.odontonotFoundAllUf;
		}

		return emptySearchState.notFoundAllUf;
	};

	if (loading || isHealthCardLoading) {
		return <Loading minHeight="50%" text={state.loadingStates} />;
	}

	if (error) {
		return (
			<BookingErrorState
				pageTitle={pageTitle}
				errorTitle={state.loadingError}
				handleBack={handleBack}
				handleRefresh={refresh}
				showHeader={false}
			/>
		);
	}

	const NOT_FOUND = !ufList || ufList?.length === 0;

	if (NOT_FOUND) {
		return (
			<Box p={isMobile ? 2 : 4} mt={2}>
				<BookingNotFoundState
					flowType={flowTypeForBookingComponents}
					pageTitle={pageTitle}
					handleBack={handleBack}
					variant={BookingNotFoundStateVariantEnum.UF}
					dataTagId={FinishWithoutCompleteFlowEnum.SEM_ESTADO_DISPONIVEL}
					showHeader={false}
				/>
			</Box>
		);
	}

	const sanitizedQuery = textHandle(query).sanitizeText();

	const FILTERED_UF_LIST = ufList?.filter(ufItem => {
		const sanitizedUf = textHandle(ufItem?.nmUf ?? '').sanitizeText();
		return sanitizedUf.includes(sanitizedQuery);
	});

	const isListEmpty = FILTERED_UF_LIST?.length === 0;
	const hasNotSpecialtiesAtCoordinate = useLocationState?.hasNotSpecialtiesAtCoordinate;
	const isCurrentStateInUfList = ufList?.some(
		uf => uf?.cdUf?.toLowerCase() === currentLocation?.state?.cdUf?.toLowerCase(),
	);

	const {LOCATION_STEP} = getHealthStepPosition({
		isManualLocation: true,
	});

	return (
		<Fragment>
			{isListEmpty ? (
				<BookingEmptyState
					description={getEmptyDescription()}
					internalPadding
					flowType={flowTypeForBookingComponents}
					dataTagId={FinishWithoutCompleteFlowEnum.SEM_ESTADO_ENCONTRADO}
				/>
			) : (
				<SelectLocationContainer
					buttonDataTagId={AppointmentBookingEnum.CLICK_NAO_ENCONTREI_ESTADO_DESEJADO}
					customOrientationText={state.orientationUf}
					handleOpenModal={() => setNotFoundModalIsOpen(true)}
					flowType={flowTypeForBookingComponents}>
					{shouldShowLocation ? (
						<Fragment>
							{hasNotSpecialtiesAtCoordinate ? (
								<AlertInfo.Root severity="warning">
									<AlertInfo.Title title={geolocNotFoundTitle} />
									<AlertInfo.Description>
										{geolocNotFoundDescription}
									</AlertInfo.Description>
								</AlertInfo.Root>
							) : null}

							{isCurrentStateInUfList && !isChangePlanFlow ? (
								<RecentLocationCard flowType={flowTypeForBookingComponents} />
							) : null}

							<Typography variant="subtitle2" color="text.secondary">
								{selectUf}
							</Typography>
						</Fragment>
					) : null}

					<StyledGrid.Root rowSpacing={2} columnSpacing={2}>
						{FILTERED_UF_LIST?.map((uf, index) => {
							const step = `${LOCATION_STEP + 1}`;
							const lastParams = `${QueryParamsEnum.STEP}=${step}&${QueryParamsForTaggingEnum.GEOLOCATION}=false`;
							const cardPath = isHealthOrExamFlow
								? `${bookingLocalityPath}?${QueryParamsEnum.UF_CODE}=${uf?.cdUf}&${QueryParamsEnum.UF_NAME}=${uf?.nmUf}&${lastParams}`
								: `${bookingCityPath}?${QueryParamsEnum.UF_CODE}=${uf?.cdUf}&${QueryParamsEnum.UF_NAME}=${uf?.nmUf}&${lastParams}`;

							return (
								<StyledGrid.Item
									sm={12}
									md={12}
									lg={FILTERED_UF_LIST.length > 1 ? 6 : 12}
									key={`${uf?.cdUf}-${index}`}>
									<Link to={cardPath} state={{pathFrom: pathname}}>
										<CardList
											dataTagId={
												ufDataTagId
													? `${ufDataTagId}${uf?.cdUf}`
													: undefined
											}
											text={formatText(uf?.nmUf)}
											showArrowRightIcon
											selected={
												isHealthOrExamFlow
													? false
													: ufCode?.toLowerCase() ===
														uf?.cdUf?.toLowerCase()
											}
										/>
									</Link>
								</StyledGrid.Item>
							);
						})}
					</StyledGrid.Root>
				</SelectLocationContainer>
			)}

			{isGuideFlow ? (
				<NotFoundDialog
					variant={NotFoundVariantEnum.UF}
					handleCallChatRoom={() => handleCallChatRoom(flowType)}
					handleGuideNav={() => handleGuideNav()}
					open={notFoundModalIsOpen}
					setOpen={setNotFoundModalIsOpen}
				/>
			) : (
				<BookingNotFoundDialog
					headerModalDataTagId={
						AppointmentBookingEnum.VISUALIZOU_NAO_ENCONTREI_ESTADO_DESEJADO
					}
					closeModalDataTagId={
						AppointmentBookingEnum.CLICK_FECHAR_MODAL_NAO_ENCONTREI_ESTADO
					}
					firstActionDataTagId={
						AppointmentBookingEnum.CLICK_CONSULTAR_REDE_MODAL_NAO_ENCONTREI_ESTADO
					}
					secondActionDataTagId={
						AppointmentBookingEnum.CLICK_FALAR_COM_ATENDIMENTO_MODAL_NAO_ENCONTREI_ESTADO
					}
					flowType={flowTypeForBookingDialog}
					variant={BookingNotFoundVariantEnum.UF}
					handleCallChatRoom={() => handleCallChatRoom(flowTypeForBookingDialog)}
					handleGuideNav={() => handleGuideNav(flowTypeForBookingDialog)}
					open={notFoundModalIsOpen}
					setOpen={setNotFoundModalIsOpen}
				/>
			)}
		</Fragment>
	);
}
